import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Hero } from "@/components/sections/Hero";
import { CTABanner } from "@/components/sections/CTABanner";
import { Reveal } from "@/components/ui/Reveal";
import { StatCounter } from "@/components/ui/StatCounter";
import { SectionHeading, RichText } from "@/components/ui/RichText";
import { Seo } from "@/components/ui/Seo";
import { ErrorBanner } from "@/components/ui/States";
import { useFetch } from "@/hooks/useFetch";
import { useSettings } from "@/context/SettingsContext";
import { localizedPath, pick } from "@/lib/helpers";
import { mediaUrl } from "@/lib/api";

/**
 * Home — renders the hero shell IMMEDIATELY (no whole-page LOADING gate).
 * Critical above-the-fold content (hero title from i18n, CTA buttons, image
 * from settings) shows on first paint; sections below the fold fill in
 * progressively as /api/pages/home and /api/services arrive in parallel.
 * On fetch failure we show a non-blocking inline banner with Retry — the
 * hero, header, and footer stay usable.
 */
export default function Home() {
  const { lang = "en" } = useParams();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { data: page, error, refetch } = useFetch(`/pages/home`, []);
  const { data: services, error: svcError, refetch: refetchSvc } = useFetch(`/services?featured=true&perPage=3`, []);

  // Only a hard error with NO renderable settings/page data shows the error page.
  // In practice settings comes from SettingsContext (loaded once at app start) so
  // we almost always have *something* to show.
  // NEVER gate the whole page on the /pages/home fetch — show inline retry instead
  // so the header / footer / hero (fed by settings) always remain usable.

  const items = services?.items || [];
  const heroImage = page?.heroImageUrl || settings.ogImageUrl;
  const heroEyebrow = pick(page?.heroEyebrow, lang);
  // Hero title comes strictly from the CMS-managed page document. We deliberately
  // do NOT fall back to settings.tagline / settings.siteName here — showing the
  // tagline briefly before the real hero title arrived was causing a "stale
  // content flash" when navigating About → Home (see bug report). On first-ever
  // visit the hero title area is briefly empty (~200 ms until /pages/home
  // resolves); on every subsequent visit the response is served from the
  // in-memory cache in useFetch, so there is no flash at all.
  const heroTitle = pick(page?.heroTitle, lang);
  const heroSubtitle = pick(page?.heroSubtitle, lang);
  const stats = page?.stats || [];
  const intro = page?.intro || {};
  const why = page?.why || [];

  // Consolidate into a single retry banner when either critical fetch has
  // failed with no data. Prevents the visual "double banner" the user reported.
  const anyErr = (error && !page) || (svcError && !services);
  const retryAll = () => {
    if (error && !page) refetch();
    if (svcError && !services) refetchSvc();
  };

  return (
    <>
      <Seo
        title={"Prime Technology and Consultancy Inc."}
        description={undefined}
        lang={lang}
        path={localizedPath(lang, "/")}
        image={mediaUrl(settings.ogImageUrl)}
      />

      <Hero
        eyebrow={heroEyebrow}
        title={heroTitle}
        subtitle={heroSubtitle}
        image={heroImage}
        tall
      >
        <Link to={localizedPath(lang, "/contact")} className="btn-primary" data-testid="hero-cta-primary">
          {t("cta.talkExpert")} <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to={localizedPath(lang, "/services")} className="btn-secondary" data-testid="hero-cta-secondary">
          {t("cta.ourServices")}
        </Link>
      </Hero>

      {/* Single inline retry banner covers both critical fetches (page + services).
         Non-blocking: the hero, header, footer and CTAs stay usable. */}
      {anyErr ? (
        <ErrorBanner
          message={t("common.errorText")}
          onRetry={retryAll}
          testId="home-content-error"
        />
      ) : null}

      {/* Stats bar — rendered only when API data arrives so we don't show empty placeholders */}
      {stats.length > 0 && (
        <section className="bg-surface border-y border-hair" data-testid="home-stats">
          <div className="container-x py-14">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
              {stats.map((s, i) => (
                <Reveal key={s.label?.en || `stat-${i}`} delay={i * 0.08} className="text-center sm:text-left">
                  <div className="font-display font-bold text-accent text-[clamp(2.5rem,5vw,4rem)] leading-none">
                    <StatCounter value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="mt-2 font-techmono text-xs uppercase tracking-widest text-mut">
                    {pick(s.label, lang)}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Intro / mission */}
      {(intro.title || intro.body) && (
        <section className="container-x py-20 md:py-28" data-testid="home-intro">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <div className="eyebrow mb-4">{pick(intro.eyebrow, lang)}</div>
              <h2 className="h-display text-fg text-[clamp(1.9rem,3.5vw+0.5rem,3rem)]">
                {pick(intro.title, lang)}
              </h2>
              <div className="mt-6">
                <RichText html={pick(intro.body, lang)} />
              </div>
              <Link
                to={localizedPath(lang, "/about")}
                className="group inline-flex items-center gap-2 mt-4 font-display uppercase tracking-widest text-accent"
                data-testid="home-about-link"
              >
                {t("nav.about")}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
            {intro.imageUrl ? (
              <Reveal delay={0.15}>
                <div className="relative">
                  <div className="absolute -inset-3 border border-cyan/20" />
                  <img
                    src={mediaUrl(intro.imageUrl, { w: 800, fmt: "webp", q: 78 })}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width="800"
                    className="relative w-full aspect-[4/3] object-cover"
                  />
                </div>
              </Reveal>
            ) : null}
          </div>
        </section>
      )}

      {/* Services preview — no separate error banner (consolidated above). */}
      {items.length > 0 && (
        <section className="bg-surface border-y border-hair py-20 md:py-28" data-testid="home-services">
          <div className="container-x">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <SectionHeading eyebrow={t("services.listingTitle")} title={t("nav.capabilities")} />
              <Link to={localizedPath(lang, "/services")} className="btn-secondary" data-testid="home-view-all-services">
                {t("cta.viewAllServices")}
              </Link>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {items.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.08}>
                  <Link
                    to={localizedPath(lang, `/services/${lang === "fr" ? s.slugFr : s.slugEn}`)}
                    className="group glass-card block h-full hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(0,212,255,0.15)]"
                    data-testid={`home-service-card-${s.slugEn}`}
                  >
                    <div className="overflow-hidden aspect-[4/3]">
                      <img
                        src={mediaUrl(s.cardImage || s.heroImage, { w: 600, fmt: "webp", q: 78 })}
                        alt={pick({ en: s.titleEn, fr: s.titleFr }, lang)}
                        loading="lazy"
                        decoding="async"
                        width="600"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <div className="eyebrow mb-2">{pick({ en: s.eyebrowEn, fr: s.eyebrowFr }, lang)}</div>
                      <h3 className="font-display font-bold uppercase text-fg text-xl tracking-wide">
                        {pick({ en: s.titleEn, fr: s.titleFr }, lang)}
                      </h3>
                      <p className="mt-3 text-mut text-sm leading-relaxed line-clamp-3">
                        {pick({ en: s.summaryEn, fr: s.summaryFr }, lang)}
                      </p>
                      <span className="mt-4 inline-flex items-center gap-1 text-accent font-techmono text-xs uppercase tracking-widest">
                        {t("cta.learnMore")} <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why choose us */}
      {why.length > 0 && (
        <section className="container-x py-20 md:py-28" data-testid="home-why">
          <SectionHeading title={pick(page.whyTitle, lang)} />
          <div className="mt-12 grid gap-px bg-line sm:grid-cols-2 lg:grid-cols-4 border border-hair">
            {why.map((w, i) => (
              <Reveal key={w.title?.en || `why-${i}`} delay={i * 0.06} className="bg-bg p-8">
                <div className="font-techmono text-accent text-sm">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-4 font-display font-bold uppercase text-fg text-lg tracking-wide">
                  {pick(w.title, lang)}
                </h3>
                <p className="mt-3 text-mut text-sm leading-relaxed">{pick(w.body, lang)}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {page?.ctaTitle ? (
        <CTABanner title={pick(page.ctaTitle, lang)} subtitle={pick(page.ctaSubtitle, lang)} image={page.heroImageUrl} />
      ) : null}
    </>
  );
}
